import { useState, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
import debounce from 'lodash/debounce';

import responsiveValue from 'js/utils/responsive-value';
import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import Button from 'components/button/Button';
import Carousel from 'components/carousel/Carousel';
import ContentContainer from 'components/content-container/ContentContainer';
import RichText from 'components/rich-text/RichText';

import TimelineHorizontalItem from './TimelineHorizontalItem';

import { ITimelineHorizontal } from 'source/NFR.types';

const themes = defineTheme({
  block: 'timeline-horizontal--theme-block'
});
type Theme = ThemeKeys<typeof themes>;

interface ResponsiveBreakpoints {
  [key: string]: number;
}

interface TimelineHorizontalProps extends ITimelineHorizontal {
  numberOfSlidesToShow?: ResponsiveBreakpoints;
  theme?: Theme;
}

const TimelineHorizontal: React.FC<TimelineHorizontalProps> = ({
  labels = {},
  numberOfSlidesToShow = {
    '0px': 1,
    '510px': 2,
    '768px': 3,
    '1024px': 4
  },
  items = [],
  startIndex = 0,
  title,
  theme
}) => {
  const DEFAULT_ITEMS = 1;
  const [isMounted, setIsMounted] = useState(false);
  const [numberOfSlidesToShowState, setNumberOfSlidesToShowState] = useState(DEFAULT_ITEMS);
  const [isVisible, setIsVisible] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const updateNumberOfSlidesToShow = useCallback(() => {
    const value = responsiveValue<number | undefined>(numberOfSlidesToShow) ?? DEFAULT_ITEMS;
    setNumberOfSlidesToShowState(value);
  }, [numberOfSlidesToShow]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setNumberOfSlidesToShowDebounced = useCallback(debounce(updateNumberOfSlidesToShow, 200, { leading: true }), [
    updateNumberOfSlidesToShow
  ]);

  useEffect(() => {
    setNumberOfSlidesToShowDebounced();
    setIsMounted(true);
    window.addEventListener('resize', setNumberOfSlidesToShowDebounced);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      window.removeEventListener('resize', setNumberOfSlidesToShowDebounced);
      observer.disconnect();
    };
  }, [setNumberOfSlidesToShowDebounced]);

  return (
    <>
      <ContentContainer className="timeline-horizontal-print">
        {title && <h2>{title}</h2>}
        {items?.map(item => (
          <div className="timeline-horizontal-print--item" key={item.title || item.subTitle}>
            {item.title && <h3 className="timeline-horizontal-print--title">{item.title}</h3>}

            {item.subTitle && <h4 className="timeline-horizontal-print--sub-title">{item.subTitle}</h4>}

            <RichText {...item.text} />
          </div>
        ))}
      </ContentContainer>
      <div
        ref={ref}
        className={cn('timeline-horizontal', themes(theme), {
          'timeline-horizontal--is-mounted': isMounted
        })}
      >
        <ContentContainer theme="wide">{title && <h2>{title}</h2>}</ContentContainer>
        <ContentContainer className="timeline-horizontal--content" theme="wide">
          <div className="timeline-horizontal--track">
            {isVisible && (
              <Carousel
                numberOfSlidesToShow={numberOfSlidesToShowState}
                startIndex={startIndex}
                previousLabel={labels.previous || 'Forrige'}
                nextLabel={labels.next || 'Neste'}
                nextButton={(next, hasNext) => (
                  <Button
                    className="timeline-horizontal--next"
                    disabled={!hasNext}
                    onClick={next}
                    title={labels.next || 'Neste'}
                    icon="arrow-rounded"
                  ></Button>
                )}
                previousButton={(previous, hasPrevious) => (
                  <Button
                    className="timeline-horizontal--previous"
                    disabled={!hasPrevious}
                    onClick={previous}
                    title={labels.previous || 'Forrige'}
                    icon="arrow-rounded"
                  ></Button>
                )}
              >
                {items?.map((item, index) => {
                  const delay = 500 * (index - startIndex + 1);
                  return (
                    <TimelineHorizontalItem
                      {...item}
                      key={index + (item.title ?? '') + item.subTitle}
                      isLast={index === items.length - 1}
                      delay={delay}
                    />
                  );
                })}
              </Carousel>
            )}
          </div>
        </ContentContainer>
      </div>
    </>
  );
};

TimelineHorizontal.displayName = 'TimelineHorizontal';

export default TimelineHorizontal;
